import { useEffect, useId, useRef } from 'react';
import './daterangepickerGlobals'; // sets window.jQuery/moment BEFORE daterangepicker loads
import $ from 'jquery';
import moment from 'moment';
import 'daterangepicker';
import 'daterangepicker/daterangepicker.css';
import '@/styles/daterange-picker.css';

// Formats mirror FinkitDatePickerConstants (legacy date-picker).
const DISPLAY = {
  date: 'DD/MM/YYYY',
  dateTime: 'DD/MM/YYYY HH:mm:ss',
  dateTimeShort: 'DD/MM/YYYY HH:mm',
} as const;
const BACKEND = {
  date: 'YYYY-MM-DD',
  dateTime: 'YYYY-MM-DDTHH:mm:ss',
} as const;

export interface RangeValue {
  start: string;
  end: string;
}

interface CommonProps {
  label?: string;
  enableTime?: boolean;
  timeSeconds?: boolean;
  minDate?: string | null;
  maxDate?: string | null;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}

type DateRangePickerProps =
  | (CommonProps & { mode: 'single'; value: string | null; onChange: (value: string) => void })
  | (CommonProps & { mode?: 'range'; value: RangeValue | null; onChange: (value: RangeValue) => void });

const PRESETS: Record<string, [moment.Moment, moment.Moment]> = {
  Today: [moment().startOf('day'), moment().endOf('day')],
  Yesterday: [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')],
  'Last 7 Days': [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')],
  'Last 30 Days': [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')],
  'This Month': [moment().startOf('month'), moment().endOf('month')],
  'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
};

// Exact port of the legacy daterangepicker (Dan Grossman) with the Finkit option
// builder + presets, wrapped for React. Values are exchanged in BACKEND format
// (YYYY-MM-DD / ...THH:mm:ss); the input displays DISPLAY format (DD/MM/YYYY).
export function DateRangePicker(props: DateRangePickerProps) {
  const { label, enableTime = false, timeSeconds, minDate, maxDate, disabled, placeholder, className, id } = props;
  const mode = props.mode ?? 'range';
  const isSingle = mode === 'single';
  const seconds = timeSeconds ?? enableTime;

  const autoId = useId();
  const fieldId = id ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  const displayFmt = enableTime ? (seconds ? DISPLAY.dateTime : DISPLAY.dateTimeShort) : DISPLAY.date;
  const backendFmt = enableTime ? BACKEND.dateTime : BACKEND.date;

  // Stable primitive deps so the picker re-inits only on config change.
  const valueKey =
    props.value == null ? '' : isSingle ? (props.value as string) : `${(props.value as RangeValue).start}|${(props.value as RangeValue).end}`;

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const $el = $(el);

    const opts: Record<string, unknown> = {
      singleDatePicker: isSingle,
      timePicker: enableTime,
      timePicker24Hour: true,
      timePickerSeconds: seconds,
      timePickerIncrement: 1,
      autoApply: isSingle,
      showDropdowns: false,
      linkedCalendars: false,
      alwaysShowCalendars: true,
      autoUpdateInput: false,
      opens: 'right',
      drops: 'auto',
      locale: {
        format: displayFmt,
        separator: ' - ',
        applyLabel: 'Apply',
        cancelLabel: 'Cancel',
        daysOfWeek: moment.weekdaysMin(),
        monthNames: moment.months(),
        firstDay: 1,
      },
      maxDate: maxDate ? moment(maxDate).endOf('day') : moment().endOf('day'),
    };
    if (minDate) opts.minDate = moment(minDate);

    if (isSingle) {
      if (props.value) opts.startDate = moment(props.value as string);
    } else if (props.value) {
      const rv = props.value as RangeValue;
      if (rv.start) opts.startDate = moment(rv.start);
      if (rv.end) opts.endDate = moment(rv.end);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($el as any).daterangepicker(opts);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instance = ($el.data('daterangepicker') as any);

    const setDisplay = () => {
      if (!instance) return;
      el.value = isSingle
        ? instance.startDate.format(displayFmt)
        : `${instance.startDate.format(displayFmt)} - ${instance.endDate.format(displayFmt)}`;
    };
    if (props.value) setDisplay();

    const emit = () => {
      if (isSingle) {
        (onChangeRef.current as (v: string) => void)(instance.startDate.format(backendFmt));
      } else {
        (onChangeRef.current as (v: RangeValue) => void)({
          start: instance.startDate.format(backendFmt),
          end: instance.endDate.format(backendFmt),
        });
      }
    };

    $el.on('apply.daterangepicker', () => {
      setDisplay();
      emit();
    });

    // Manual typing: parse what the user typed (strict, in the display format) and
    // apply it to the picker, then emit. Revert to the last valid value if invalid.
    const commitTyped = () => {
      if (!instance) return;
      const raw = (el.value || '').trim();
      if (!raw) return;
      if (isSingle) {
        const m = moment(raw, displayFmt, true);
        if (m.isValid()) {
          instance.setStartDate(m);
          instance.setEndDate(m);
          setDisplay();
          // Only emit on a real change, so merely opening/closing the picker doesn't
          // fire onChange (which some pages treat as "the user changed the date").
          if (m.format(backendFmt) !== (props.value as string | null)) emit();
        } else {
          setDisplay();
        }
        return;
      }
      const parts = raw.split(' - ');
      const s = moment(parts[0]?.trim() ?? '', displayFmt, true);
      const e = moment((parts[1] ?? parts[0])?.trim() ?? '', displayFmt, true);
      if (s.isValid() && e.isValid() && !e.isBefore(s)) {
        instance.setStartDate(s);
        instance.setEndDate(e);
        setDisplay();
        const rv = props.value as RangeValue | null;
        if (!rv || rv.start !== s.format(backendFmt) || rv.end !== e.format(backendFmt)) emit();
      } else {
        setDisplay();
      }
    };
    $el.on('blur.drpType', commitTyped);
    // Also commit whatever is typed when the picker CLOSES (e.g. clicking outside the
    // box without pressing Apply) — otherwise the typed value is silently dropped and
    // a following Fetch runs with the stale value.
    $el.on('hide.daterangepicker', commitTyped);
    $el.on('keydown.drpType', (ev) => {
      if ((ev as unknown as KeyboardEvent).key === 'Enter') {
        ev.preventDefault();
        commitTyped();
        instance?.hide?.();
      }
    });

    // Presets (mirrors FinkitDatePickerPresetsHelper).
    $el.on('show.daterangepicker', (_ev, picker) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const $c = $((picker as any).container);
      if (isSingle) {
        if ($c.find('.drp-single-presets').length) return;
        const $bar = $('<div class="drp-single-presets d-flex justify-content-end py-1 px-2 border-top"></div>');
        const $today = $('<button type="button" class="btn btn-link btn-md">Today</button>');
        $today.on('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const t = moment().startOf('day');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (picker as any).setStartDate(t);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (picker as any).setEndDate(t);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (picker as any).clickApply();
        });
        $bar.append($today);
        $c.find('.drp-calendar.left').append($bar);
      } else {
        if ($c.find('.drp-presets-wrap').length) return;
        const $wrap = $('<div class="drp-presets-wrap dropup" style="display:inline-block;margin-left:auto;"></div>');
        const $btn = $('<button class="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">Period</button>');
        const $menu = $('<ul class="dropdown-menu dropdown-menu-end" style="min-width:150px;"></ul>');
        Object.keys(PRESETS).forEach((lbl) => {
          const $item = $(`<li><a class="dropdown-item" href="#">${lbl}</a></li>`);
          $item.find('a').on('click', (e) => {
            e.preventDefault();
            const [s, en] = PRESETS[lbl];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (picker as any).setStartDate(s.clone());
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (picker as any).setEndDate(en.clone());
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (picker as any).updateCalendars();
            $btn.text(lbl);
          });
          $menu.append($item);
        });
        $wrap.append($btn).append($menu);
        const $apply = $c.find('.drp-buttons .applyBtn');
        if ($apply.length) $apply.after($wrap);
        else $c.find('.drp-buttons').append($wrap);
      }
    });

    return () => {
      $el.off('apply.daterangepicker show.daterangepicker hide.daterangepicker blur.drpType keydown.drpType');
      if (instance) instance.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingle, enableTime, seconds, displayFmt, backendFmt, minDate, maxDate, valueKey]);

  return (
    <div className={className || undefined}>
      {label && (
        <label htmlFor={fieldId} className="form-label">
          {label}
        </label>
      )}
      <div className="input-group">
        <input
          id={fieldId}
          ref={inputRef}
          type="text"
          className="form-control"
          disabled={disabled}
          placeholder={placeholder ?? (isSingle ? 'Select date' : 'Select date range')}
        />
        <span className="input-group-text" style={{ cursor: 'pointer' }} onClick={() => inputRef.current?.click()}>
          <i className="ri-calendar-line" />
        </span>
      </div>
    </div>
  );
}
